import { useState, useEffect } from 'react';
import { Wallet, Clock, Tag, AlertCircle, Upload as UploadIcon } from 'lucide-react';
import { Modal, Button, Input, Textarea } from '../common';
import { updateRestaurant } from '../../services/restaurantService';
import { supabase } from '../../services/supabaseClient';

const ADMIN_REGISTRATION_FEE = 20; // Admin pays 20 AED per restaurant
const FULL_SIGNUP_FEE = 300;
const DISCOUNTED_SIGNUP_FEE = 150; // 50% off for immediate payment

// Upload file to R2 storage
async function uploadFile(file, field) {
  const WORKER = import.meta.env.VITE_R2_UPLOAD_ENDPOINT || 'https://crm.gosnel.com/api/upload';
  const fd = new FormData();
  fd.append('file', file);
  fd.append('field', field);
  const res = await fetch(WORKER, { method: 'POST', body: fd });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return (await res.json()).url;
}

/**
 * Onboarding Modal - Marketer fills restaurant details
 * Business Logic:
 * - Marketers/Reps get 5 FREE registrations (credits)
 * - FREE registration (pay_on_topup) → Uses 1 credit
 * - PAID registration (immediate) → Does NOT use credit
 * - Admin pays 20 AED per restaurant (no commissions)
 */
export function OnboardingModal({ restaurant, isOpen, onClose, showToast, currentUser, onUpdateSuccess }) {
  const [formData, setFormData] = useState({
    ownerName: restaurant?.ownerName || "",
    ownerPhone: restaurant?.ownerPhone || "",
    whatsappNumber: restaurant?.whatsappNumber || "",
    taxNumber: restaurant?.taxNumber || "",
    commissionPercentage: restaurant?.commissionPercentage || 15,
    signupFee: restaurant?.signupFee || ADMIN_REGISTRATION_FEE,
    deliveryRadiusKm: restaurant?.deliveryRadiusKm || 0,
    defaultDeliveryFee: restaurant?.defaultDeliveryFee || 0,
    minimumOrderAmount: restaurant?.minimumOrderAmount || 0,
    discountOfferFirst5: restaurant?.discountOfferFirst5 || "",
    openTime: restaurant?.openTime || "09:00",
    closeTime: restaurant?.closeTime || "23:00",
    visitDate: restaurant?.visitDate || "",
    visitTime: restaurant?.visitTime || "",
    visitNotes: restaurant?.visitNotes || "",
    notes: restaurant?.notes || "",
    paymentMethod: restaurant?.paymentMethod || 'immediate',
    // Supplier Interest Fields
    estimatedMonthlyPurchase: restaurant?.estimatedMonthlyPurchase || "",
    deliveryFrequency: restaurant?.deliveryFrequency || "none",
    interestedCategories: restaurant?.interestedCategories || [],
  });
  const [freeRegistrationsRemaining, setFreeRegistrationsRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // File upload states
  const [uploadingFile, setUploadingFile] = useState(null);
  const [files, setFiles] = useState({
    logoUrl: restaurant?.files?.logoUrl || '',
    menu1Url: restaurant?.files?.menu1Url || '',
    ownerIdUrl: restaurant?.files?.ownerIdUrl || '',
    tradeLicenseUrl: restaurant?.files?.tradeLicenseUrl || '',
  });

  // Load free registrations count on mount
  useEffect(() => {
    const loadFreeRegistrations = async () => {
      if (!currentUser?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('users')
          .select('free_registrations_remaining')
          .eq('id', currentUser.id)
          .single();
        
        if (error) throw error;
        setFreeRegistrationsRemaining(data?.free_registrations_remaining ?? 0);
      } catch (err) {
        console.error('Load free registrations error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadFreeRegistrations();
    }
  }, [currentUser?.id, isOpen]);

  // Auto-update signup fee when payment method changes
  const handlePaymentMethodChange = (method) => {
    const newFee = method === 'immediate' ? DISCOUNTED_SIGNUP_FEE : FULL_SIGNUP_FEE;
    setFormData(prev => ({ ...prev, paymentMethod: method, signupFee: newFee }));
  };

  // Handle file upload
  const handleFileUpload = async (e, fileKey, label) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFile(fileKey);
    try {
      const url = await uploadFile(file, fileKey);
      setFiles(prev => ({ ...prev, [fileKey]: url }));
      showToast(`✅ تم رفع ${label} بنجاح`);
    } catch (err) {
      console.error(`Upload ${fileKey} error:`, err);
      showToast(`❌ فشل في رفع ${label}`, 'error');
    } finally {
      setUploadingFile(null);
      e.target.value = '';
    }
  };
  
  const [submitting, setSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  // Check if form has been modified
  const hasChanges = () => {
    return (
      formData.ownerName !== (restaurant?.ownerName || "") ||
      formData.ownerPhone !== (restaurant?.ownerPhone || "") ||
      formData.whatsappNumber !== (restaurant?.whatsappNumber || "") ||
      formData.taxNumber !== (restaurant?.taxNumber || "") ||
      formData.commissionPercentage !== (restaurant?.commissionPercentage || 15) ||
      formData.signupFee !== (restaurant?.signupFee || 150) ||
      formData.deliveryRadiusKm !== (restaurant?.deliveryRadiusKm || 0) ||
      formData.defaultDeliveryFee !== (restaurant?.defaultDeliveryFee || 0) ||
      formData.minimumOrderAmount !== (restaurant?.minimumOrderAmount || 0) ||
      formData.discountOfferFirst5 !== (restaurant?.discountOfferFirst5 || "") ||
      formData.openTime !== (restaurant?.openTime || "09:00") ||
      formData.closeTime !== (restaurant?.closeTime || "23:00") ||
      formData.visitDate !== (restaurant?.visitDate || "") ||
      formData.visitTime !== (restaurant?.visitTime || "") ||
      formData.visitNotes !== (restaurant?.visitNotes || "") ||
      formData.notes !== (restaurant?.notes || "") ||
      formData.estimatedMonthlyPurchase !== (restaurant?.estimatedMonthlyPurchase || "") ||
      formData.deliveryFrequency !== (restaurant?.deliveryFrequency || "none") ||
      JSON.stringify(formData.interestedCategories) !== JSON.stringify(restaurant?.interestedCategories || [])
    );
  };

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Toggle product category
  const toggleCategory = (category) => {
    setFormData(prev => {
      const categories = prev.interestedCategories || [];
      if (categories.includes(category)) {
        return { ...prev, interestedCategories: categories.filter(c => c !== category) };
      } else {
        return { ...prev, interestedCategories: [...categories, category] };
      }
    });
  };

  // Save as Draft and Close
  const saveDraft = async () => {
    setSubmitting(true);
    try {
      // Clean up empty date/time fields to avoid DB errors
      const cleanedData = { 
        ...formData,
        files: {
          logoUrl: files.logoUrl || null,
          menu1Url: files.menu1Url || null,
          ownerIdUrl: files.ownerIdUrl || null,
          tradeLicenseUrl: files.tradeLicenseUrl || null,
        },
      };
      if (!cleanedData.visitDate) delete cleanedData.visitDate;
      if (!cleanedData.visitTime) delete cleanedData.visitTime;
      
      await updateRestaurant(restaurant.id, cleanedData, currentUser);
      showToast("💾 تم الحفظ والإغلاق بنجاح");
      
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      
      onClose();
    } catch (err) {
      console.error('Draft save error:', err);
      showToast('❌ فشل في الحفظ', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit to Data Entry (with confirmation)
  const handleSubmitClick = () => {
    // Validate required fields first
    const required = [
      { key: 'ownerName', label: 'اسم المالك' },
      { key: 'ownerPhone', label: 'رقم هاتف المالك' },
      { key: 'taxNumber', label: 'الرقم الضريبي' },
    ];

    const incomplete = required.filter(field => !formData[field.key]);
    
    if (incomplete.length > 0) {
      const fieldNames = incomplete.map(f => f.label).join('، ');
      showToast(`❌ يرجى إكمال: ${fieldNames}`, 'error');
      return;
    }
    
    // Show confirmation dialog
    setShowSubmitConfirm(true);
  };

  // Actual submit after confirmation
  const submitToDataEntry = async () => {
    setShowSubmitConfirm(false);
    setSubmitting(true);
    try {
      const isFreeRegistration = formData.paymentMethod === 'pay_on_topup';
      
      // Deduct free registration credit ONLY for FREE registrations (pay_on_topup)
      if ((currentUser?.role === 'marketer' || currentUser?.role === 'rep') && isFreeRegistration) {
        if (freeRegistrationsRemaining <= 0) {
          showToast('❌ لا يوجد لديك تسجيلات مجانية متبقية. يجب على المطعم الدفع!', 'error');
          setSubmitting(false);
          return;
        }

        const { error: userError } = await supabase
          .from('users')
          .update({ free_registrations_remaining: freeRegistrationsRemaining - 1 })
          .eq('id', currentUser.id);
        
        if (userError) throw userError;
      }

      // Clean up empty date/time fields to avoid DB errors
      const cleanedData = { 
        ...formData,
        files: {
          logoUrl: files.logoUrl || null,
          menu1Url: files.menu1Url || null,
          ownerIdUrl: files.ownerIdUrl || null,
          tradeLicenseUrl: files.tradeLicenseUrl || null,
        },
        status: 'data_review' // Move to next stage
      };
      if (!cleanedData.visitDate) delete cleanedData.visitDate;
      if (!cleanedData.visitTime) delete cleanedData.visitTime;
      
      await updateRestaurant(
        restaurant.id, 
        cleanedData,
        currentUser
      );
      
      // Show appropriate success message
      if (currentUser?.role === 'admin') {
        showToast("✅ تم التسجيل بنجاح (رسوم 20 AED)");
      } else if (isFreeRegistration) {
        const remaining = freeRegistrationsRemaining - 1;
        showToast(`✅ تم التسجيل مجاناً (متبقي ${remaining} تسجيل مجاني)`);
      } else {
        showToast("✅ تم الإرسال إلى مدخل البيانات (العميل سيدفع)");
      }
      
      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      
      onClose();
    } catch (err) {
      console.error('Submit error:', err);
      showToast('❌ فشل في الإرسال', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    if (hasChanges()) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  // Discard changes and close
  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  if (!restaurant) return null;

  return (
    <Modal 
      title={`📋 إدخال بيانات: ${restaurant.name}`} 
      isOpen={isOpen} 
      onClose={onClose}
      size="lg"
    >
      <div style={{ direction: "rtl" }}>
        {/* Free Registrations Banner for Marketers/Reps */}
        {(currentUser?.role === 'marketer' || currentUser?.role === 'rep') && freeRegistrationsRemaining !== null && (
          <div style={{
            marginBottom: 20,
            padding: 16,
            background: freeRegistrationsRemaining > 0 
              ? 'linear-gradient(135deg, #10b98122, #10b98111)' 
              : 'linear-gradient(135deg, #ef444422, #ef444411)',
            border: `2px solid ${freeRegistrationsRemaining > 0 ? '#10b98144' : '#ef444444'}`,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontSize: 32 }}>
              {freeRegistrationsRemaining > 0 ? '🎁' : '⚠️'}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ 
                color: freeRegistrationsRemaining > 0 ? '#10b981' : '#ef4444',
                fontSize: 16,
                fontWeight: 900,
                marginBottom: 4,
              }}>
                {freeRegistrationsRemaining > 0 
                  ? `متبقي ${freeRegistrationsRemaining} تسجيل مجاني` 
                  : 'نفذت التسجيلات المجانية!'}
              </div>
              <div style={{ color: '#000000', fontSize: 13 }}>
                {freeRegistrationsRemaining > 0
                  ? 'التسجيل المجاني: العميل لا يدفع، يتم استخدام 1 من رصيدك المجاني'
                  : 'يجب إقناع المطعم بالدفع (150 AED بخصم 50%)'}
              </div>
            </div>
          </div>
        )}

        {/* Package Selection */}
        {(currentUser?.role === 'marketer' || currentUser?.role === 'rep' || currentUser?.role === 'sales_manager' || currentUser?.role === 'admin') && (
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              📦 اختر الباقة
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {/* Gold Package */}
              <button
                onClick={() => handlePaymentMethodChange('immediate')}
                style={{
                  padding: 20,
                  background: formData.paymentMethod === 'immediate' ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.1))' : '#ffffff',
                  border: `3px solid ${formData.paymentMethod === 'immediate' ? '#fbbf24' : '#e2e8f0'}`,
                  borderRadius: 12,
                  cursor: 'pointer',
                  textAlign: 'right',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {formData.paymentMethod === 'immediate' && (
                  <div style={{
                    position: 'absolute',
                    top: -12,
                    right: 12,
                    background: '#fbbf24',
                    color: '#000',
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 900,
                  }}>
                    ✓ مختار
                  </div>
                )}
                <div style={{ fontSize: 28, marginBottom: 8 }}>👑</div>
                <div style={{ color: '#f59e0b', fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
                  الباقة الذهبية
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                  <span style={{ color: '#10b981', fontSize: 24, fontWeight: 900 }}>210 AED</span>
                  <span style={{ textDecoration: 'line-through', color: '#64748b', fontSize: 14 }}>700 AED</span>
                  <span style={{ background: '#10b981', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>خصم 70%</span>
                </div>
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, fontSize: 12, lineHeight: 2 }}>
                  <div style={{ color: '#64748b' }}>✅ <strong style={{ color: '#0f172a' }}>50 AED</strong> رصيد محفظة مجاني</div>
                  <div style={{ color: '#64748b' }}>✅ <strong style={{ color: '#0f172a' }}>إعلان مخصص</strong> (Dedicated)</div>
                  <div style={{ color: '#64748b' }}>✅ <strong style={{ color: '#0f172a' }}>قائمة طعام كاملة</strong> بلا حدود</div>
                  <div style={{ color: '#64748b' }}>✅ عمولة <strong style={{ color: '#0f172a' }}>15%</strong> فقط</div>
                  <div style={{ color: '#64748b' }}>✅ توصيل عبر <strong style={{ color: '#0f172a' }}>WhatsApp/GoSnel</strong></div>
                </div>
              </button>

              {/* Free Package */}
              <button
                onClick={() => {
                  if (freeRegistrationsRemaining <= 0) {
                    showToast('❌ لا يوجد رصيد مجاني متبقي!', 'error');
                    return;
                  }
                  handlePaymentMethodChange('pay_on_topup');
                }}
                disabled={freeRegistrationsRemaining <= 0}
                style={{
                  padding: 20,
                  background: formData.paymentMethod === 'pay_on_topup' ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.1))' : '#ffffff',
                  border: `3px solid ${formData.paymentMethod === 'pay_on_topup' ? '#3b82f6' : freeRegistrationsRemaining <= 0 ? '#cbd5e1' : '#e2e8f0'}`,
                  borderRadius: 12,
                  cursor: freeRegistrationsRemaining <= 0 ? 'not-allowed' : 'pointer',
                  opacity: freeRegistrationsRemaining <= 0 ? 0.5 : 1,
                  textAlign: 'right',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {formData.paymentMethod === 'pay_on_topup' && (
                  <div style={{
                    position: 'absolute',
                    top: -12,
                    right: 12,
                    background: '#3b82f6',
                    color: '#fff',
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 900,
                  }}>
                    ✓ مختار
                  </div>
                )}
                <div style={{ fontSize: 28, marginBottom: 8 }}>🎁</div>
                <div style={{ color: '#3b82f6', fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
                  الباقة المجانية
                </div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ color: '#3b82f6', fontSize: 24, fontWeight: 900 }}>مجاني</span>
                  <div style={{ color: '#000000', fontSize: 11, marginTop: 4 }}>للأبد - العميل لا يدفع شيء</div>
                </div>
                <div style={{ borderTop: '1px solid #1e3a5f', paddingTop: 12, fontSize: 12, lineHeight: 2 }}>
                  <div style={{ color: '#000000' }}>✅ <strong style={{ color: '#000000' }}>50 AED</strong> رصيد محفظة مجاني</div>
                  <div style={{ color: '#000000' }}>✅ <strong style={{ color: '#000000' }}>إعلان ضمن الحملات العامة</strong></div>
                  <div style={{ color: '#000000' }}>⚠️ <strong style={{ color: '#f59e0b' }}>3 مجموعات كومبو</strong> فقط*</div>
                  <div style={{ color: '#000000', fontSize: 11, paddingRight: 20 }}>3 رئيسي + 2 جانبي + 2 مشروب + 2 حلويات</div>
                  <div style={{ color: '#000000' }}>✅ عمولة <strong style={{ color: '#000000' }}>15%</strong></div>
                  <div style={{ color: '#000000' }}>✅ توصيل عبر <strong style={{ color: '#000000' }}>WhatsApp</strong></div>
                </div>
                {freeRegistrationsRemaining > 0 && (
                  <div style={{ marginTop: 12, color: '#10b981', fontSize: 11, fontWeight: 700 }}>
                    -1 من رصيدك المجاني (متبقي {freeRegistrationsRemaining})
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Section 1: Owner Information */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            👤 معلومات المالك
          </h3>
          
          <Input
            label="اسم المالك *"
            value={formData.ownerName}
            onChange={e => updateField('ownerName', e.target.value)}
            placeholder="محمد علي"
          />

          <Input
            label="رقم هاتف المالك *"
            type="tel"
            value={formData.ownerPhone}
            onChange={e => updateField('ownerPhone', e.target.value)}
            placeholder="+971 50 123 4567"
          />

          <Input
            label="رقم الواتساب (اختياري)"
            type="tel"
            value={formData.whatsappNumber}
            onChange={e => updateField('whatsappNumber', e.target.value)}
            placeholder="+971 50 123 4567"
          />

          <Input
            label="الرقم الضريبي *"
            value={formData.taxNumber}
            onChange={e => updateField('taxNumber', e.target.value)}
            placeholder="123456789012345"
          />
        </div>

        {/* Files Upload Section */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            📎 الملفات والمستندات
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Logo Upload */}
            <FileUploadCard
              label="شعار المطعم"
              icon="🖼️"
              fileUrl={files.logoUrl}
              uploading={uploadingFile === 'logoUrl'}
              onChange={e => handleFileUpload(e, 'logoUrl', 'الشعار')}
            />

            {/* Menu Upload */}
            <FileUploadCard
              label="قائمة الطعام"
              icon="📄"
              fileUrl={files.menu1Url}
              uploading={uploadingFile === 'menu1Url'}
              onChange={e => handleFileUpload(e, 'menu1Url', 'قائمة الطعام')}
            />

            {/* Owner ID Upload */}
            <FileUploadCard
              label="هوية المالك"
              icon="🆔"
              fileUrl={files.ownerIdUrl}
              uploading={uploadingFile === 'ownerIdUrl'}
              onChange={e => handleFileUpload(e, 'ownerIdUrl', 'الهوية')}
            />

            {/* Trading License Upload */}
            <FileUploadCard
              label="الرخصة التجارية"
              icon="📜"
              fileUrl={files.tradeLicenseUrl}
              uploading={uploadingFile === 'tradeLicenseUrl'}
              onChange={e => handleFileUpload(e, 'tradeLicenseUrl', 'الرخصة التجارية')}
            />
          </div>
        </div>

        {/* Section 2: Financial Details */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            💰 التفاصيل المالية
          </h3>
          
          <Input
            label="نسبة العمولة (%)"
            type="number"
            value={formData.commissionPercentage}
            onChange={e => updateField('commissionPercentage', parseFloat(e.target.value) || 0)}
            placeholder={formData.paymentMethod === 'immediate' ? '15' : '20'}
          />
        </div>

        {/* Section 3: Delivery Settings */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            🚚 إعدادات التوصيل
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {/* Delivery Radius - Dropdown */}
            <div>
              <label style={{ color: '#000000', fontSize: 12, fontWeight: 700, marginBottom: 6, display: 'block' }}>
                نطاق التوصيل (كم)
              </label>
              <select
                value={formData.deliveryRadiusKm}
                onChange={e => updateField('deliveryRadiusKm', parseFloat(e.target.value) || 0)}
                style={{
                  width: '100%',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#0f172a',
                  fontSize: 14,
                  fontFamily: "'Cairo',sans-serif",
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
              >
                <option value={0}>0 كم</option>
                <option value={1}>1 كم</option>
                <option value={3}>3 كم</option>
                <option value={5}>5 كم</option>
                <option value={7}>7 كم</option>
              </select>
            </div>

            <Input
              label="رسوم التوصيل الافتراضية"
              type="number"
              value={formData.defaultDeliveryFee}
              onChange={e => updateField('defaultDeliveryFee', parseFloat(e.target.value) || 0)}
              placeholder="15"
            />

            <Input
              label="الحد الأدنى للطلب"
              type="number"
              value={formData.minimumOrderAmount}
              onChange={e => updateField('minimumOrderAmount', parseFloat(e.target.value) || 0)}
              placeholder="50"
            />
          </div>
        </div>

        {/* Business Hours - Open & Close Time */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            🕐 ساعات العمل
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input
              label="وقت الافتتاح"
              type="time"
              value={formData.openTime}
              onChange={e => updateField('openTime', e.target.value)}
            />
            <Input
              label="وقت الإغلاق"
              type="time"
              value={formData.closeTime}
              onChange={e => updateField('closeTime', e.target.value)}
            />
          </div>
        </div>

        {/* Section 4: Visit Scheduling */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            📅 جدولة الزيارة
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Input
              label="تاريخ الزيارة"
              type="date"
              value={formData.visitDate}
              onChange={e => updateField('visitDate', e.target.value)}
            />

            <Input
              label="وقت الزيارة"
              type="time"
              value={formData.visitTime}
              onChange={e => updateField('visitTime', e.target.value)}
            />
          </div>

          <Textarea
            label="ملاحظات الزيارة"
            value={formData.visitNotes}
            onChange={e => updateField('visitNotes', e.target.value)}
            placeholder="ملاحظات إضافية حول الزيارة..."
            rows={3}
          />
        </div>

        {/* Section 6: Supplier Interest (Wholesale Products) */}
        <div style={{ 
          marginBottom: 24, 
          padding: 20, 
          background: '#f0fdfa', 
          border: '2px solid #4ecdc4', 
          borderRadius: 12 
        }}>
          <h3 style={{ color: "#000000", fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
            📦 اهتمام المطعم بالموردين (منتجات الجملة)
          </h3>
          
          {/* Estimated Monthly Purchase */}
          <div style={{ marginBottom: 16 }}>
            <Input
              label="الشراء الشهري المتوقع (AED)"
              type="number"
              value={formData.estimatedMonthlyPurchase}
              onChange={e => updateField('estimatedMonthlyPurchase', e.target.value)}
              placeholder="مثال: 5000"
            />
          </div>

          {/* Delivery Frequency */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#000000', fontSize: 12, fontWeight: 700, marginBottom: 6, display: 'block' }}>
              تكرار التوصيل
            </label>
            <select
              value={formData.deliveryFrequency}
              onChange={e => updateField('deliveryFrequency', e.target.value)}
              style={{
                width: '100%',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '10px 12px',
                color: '#0f172a',
                fontSize: 14,
                fontFamily: "'Cairo',sans-serif",
                cursor: 'pointer',
              }}
            >
              <option value="none">بدون</option>
              <option value="daily">يومي</option>
              <option value="weekly">أسبوعي</option>
              <option value="monthly">شهري</option>
            </select>
          </div>

          {/* Interested Product Categories */}
          <div>
            <label style={{ color: '#000000', fontSize: 12, fontWeight: 700, marginBottom: 10, display: 'block' }}>
              فئات المنتجات المهتم بها *
            </label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', 
              gap: 10 
            }}>
              {[
                'لحوم',
                'خضار وفواكه',
                'أرز وسكر',
                'زيوت',
                'ألبان وأجبان',
                'مشروبات',
                'معلبات',
                'بهارات',
                'فحم شوي',
              ].map(category => {
                const isChecked = formData.interestedCategories.includes(category);
                return (
                  <label
                    key={category}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 12px',
                      background: isChecked ? '#4ecdc4' : '#ffffff',
                      border: `2px solid ${isChecked ? '#4ecdc4' : '#e2e8f0'}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      color: isChecked ? '#ffffff' : '#000000',
                      fontWeight: isChecked ? 700 : 500,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleCategory(category)}
                      style={{
                        width: 16,
                        height: 16,
                        accentColor: '#4ecdc4',
                        cursor: 'pointer',
                      }}
                    />
                    {category}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Section 5: Additional Notes */}
        <Textarea
          label="ملاحظات عامة"
          value={formData.notes}
          onChange={e => updateField('notes', e.target.value)}
          placeholder="أي معلومات إضافية..."
          rows={3}
        />

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <Button 
            onClick={handleSubmitClick} 
            disabled={submitting}
            style={{ flex: 2, justifyContent: "center" }}
          >
            {submitting ? "جاري الإرسال..." : "✅ إرسال إلى مدخل البيانات"}
          </Button>
          <Button 
            variant="secondary" 
            onClick={saveDraft}
            disabled={submitting}
            style={{ flex: 1, justifyContent: "center" }}
          >
            💾 حفظ وإغلاق
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleCancelClick}
            disabled={submitting}
            style={{ flex: 1, justifyContent: "center" }}
          >
            إلغاء
          </Button>
        </div>

        {/* Submit Confirmation Modal */}
        {showSubmitConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}>
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 16,
              padding: 24,
              maxWidth: 450,
              width: '90%',
              direction: 'rtl',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
                <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                  تأكيد الإرسال
                </h3>
                <p style={{ color: '#000000', fontSize: 14, lineHeight: 1.6 }}>
                  هل أنت متأكد من إرسال البيانات إلى مدخل البيانات؟<br/>
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>سيتم تغيير الحالة ولا يمكن التراجع</span>
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <Button 
                  onClick={submitToDataEntry}
                  disabled={submitting}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  {submitting ? 'جاري الإرسال...' : '✅ نعم، إرسال'}
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => setShowSubmitConfirm(false)}
                  disabled={submitting}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  ❌ إلغاء
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Discard Changes Confirmation Modal */}
        {showDiscardConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}>
            <div style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 16,
              padding: 24,
              maxWidth: 450,
              width: '90%',
              direction: 'rtl',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
                <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                  لديك تغييرات غير محفوظة
                </h3>
                <p style={{ color: '#000000', fontSize: 14, lineHeight: 1.6 }}>
                  هل تريد حفظ التغييرات قبل الإغلاق؟
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <Button 
                  onClick={saveDraft}
                  disabled={submitting}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  💾 حفظ
                </Button>
                <Button 
                  variant="secondary"
                  onClick={handleDiscard}
                  disabled={submitting}
                  style={{ flex: 1, justifyContent: 'center', background: '#e2e8f0', color: '#000000' }}
                >
                  ❌ تجاهل
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => setShowDiscardConfirm(false)}
                  disabled={submitting}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  رجوع
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// File Upload Card Component
function FileUploadCard({ label, icon, fileUrl, uploading, onChange }) {
  const fileId = label.replace(/\s+/g, '-');
  
  return (
    <div style={{
      background: '#f8fafc',
      border: `2px dashed ${fileUrl ? '#10b981' : '#cbd5e1'}`,
      borderRadius: 10,
      padding: 16,
      textAlign: 'center',
      transition: 'all 0.2s',
    }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <div style={{ color: '#000000', fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
        {label}
      </div>

      {fileUrl ? (
        <div>
          <div style={{
            background: '#f0fdfa',
            color: '#10b981',
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 6,
            marginBottom: 8,
            display: 'inline-block',
            fontWeight: 700,
          }}>
            ✅ تم الرفع
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: '#4ecdc4',
                color: '#ffffff',
                fontSize: 11,
                padding: '4px 10px',
                borderRadius: 6,
                textDecoration: 'none',
                fontWeight: 700,
              }}
            >
              👁️ عرض
            </a>
            <label
              htmlFor={fileId}
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                color: '#000000',
                fontSize: 11,
                padding: '4px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              🔄 تغيير
            </label>
          </div>
        </div>
      ) : (
        <label
          htmlFor={fileId}
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: uploading ? '#f0fdfa' : '#ffffff',
            border: '1px solid #e2e8f0',
            color: uploading ? '#10b981' : '#64748b',
            borderRadius: 8,
            cursor: uploading ? 'wait' : 'pointer',
            fontSize: 12,
            fontWeight: 700,
            transition: 'all 0.2s',
          }}
        >
          {uploading ? '⏳ جاري الرفع...' : '📤 رفع ملف'}
        </label>
      )}

      <input
        id={fileId}
        type="file"
        onChange={onChange}
        disabled={uploading}
        style={{ display: 'none' }}
        accept="image/*,.pdf,.doc,.docx"
      />
    </div>
  );
}
